import {
  CalendarIcon,
  CodeIcon,
  CursorClickIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  MailIcon,
  SearchIcon,
  TerminalIcon,
} from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';
import React from 'react';

export default function ActionsSection() {
  const { t } = useLocale('chatbots');

  const actions = [
    {
      name: 'Custom Action',
      description: 'Execute custom code or integrate with other systems',
      icon: <CodeIcon className='h-6 w-6' />,
      bgColor: 'bg-purple-100 dark:bg-purple-900',
      iconColor: 'text-purple-600 dark:text-purple-300',
    },
    {
      name: 'Custom Button',
      description: 'Create custom buttons within the AI agent interface',
      icon: <CursorClickIcon className='h-6 w-6' />,
      bgColor: 'bg-blue-100 dark:bg-blue-900',
      iconColor: 'text-blue-600 dark:text-blue-300',
    },
    {
      name: 'Custom Form',
      description:
        'Create and display interactive forms to collect user information',
      icon: <DocumentTextIcon className='h-6 w-6' />,
      bgColor: 'bg-pink-100 dark:bg-pink-900',
      iconColor: 'text-pink-600 dark:text-pink-300',
    },
    {
      name: 'Fetch URL',
      description:
        'Retrieve data from external websites or APIs during conversations',
      icon: <GlobeAltIcon className='h-6 w-6' />,
      bgColor: 'bg-indigo-100 dark:bg-indigo-900',
      iconColor: 'text-indigo-600 dark:text-indigo-300',
    },
    {
      name: 'Code Interpreter',
      description:
        'Enables your bot to run Python code, analyze data, generate charts, and solve complex calculations',
      icon: <TerminalIcon className='h-6 w-6' />,
      bgColor: 'bg-emerald-100 dark:bg-emerald-900',
      iconColor: 'text-emerald-600 dark:text-emerald-300',
    },
    {
      name: 'Web Search',
      description: 'Perform real-time web searches for up-to-date information',
      icon: <SearchIcon className='h-6 w-6' />,
      bgColor: 'bg-amber-100 dark:bg-amber-900',
      iconColor: 'text-amber-600 dark:text-amber-300',
    },
    {
      name: 'Email',
      description: 'Send email notifications based on user interactions',
      icon: <MailIcon className='h-6 w-6' />,
      bgColor: 'bg-red-100 dark:bg-red-900',
      iconColor: 'text-red-600 dark:text-red-300',
    },
    {
      name: 'Google Calendar',
      description: 'Create and manage calendar events during conversations',
      icon: <CalendarIcon className='h-6 w-6' />,
      bgColor: 'bg-teal-100 dark:bg-teal-900',
      iconColor: 'text-teal-600 dark:text-teal-300',
    },
  ];

  return (
    <section className='bg-gray-50 py-24 dark:bg-slate-800 sm:py-32'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <div className='mx-auto max-w-2xl text-center'>
          <h2 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl'>
            {t('Give your agent the power to act')}
          </h2>
          <p className='mt-6 text-lg leading-8 text-gray-600 dark:text-gray-400'>
            {t(
              'Beyond answering questions: your agent can run code, search the web, send emails, book meetings, and call your APIs mid-conversation',
            )}
          </p>
        </div>
        <div className='mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-6 sm:mt-20 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:grid-cols-3'>
          {actions.map((action) => (
            <div
              key={action.name}
              className='group relative overflow-hidden rounded-lg border bg-white p-6 transition-shadow duration-300 ease-in-out hover:bg-gray-50 hover:shadow-lg dark:border-slate-700 dark:bg-slate-800 dark:hover:border-primary-600 dark:hover:bg-slate-900'
            >
              <div className='flex items-center space-x-3'>
                <div
                  className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${action.bgColor} ${action.iconColor}`}
                >
                  {action.icon}
                </div>
                <h3 className='text-lg font-medium text-gray-900 dark:text-white'>
                  {t(action.name)}
                </h3>
              </div>
              <p className='mt-4 text-sm text-gray-500 dark:text-gray-400'>
                {t(action.description)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
